/**
 * ThemeSettledScreen — un écran de l'espace élève monté dans le thème où il sera peint.
 *
 * Ouvert à froid (un lien direct, une page rechargée), l'écran monte dans le même rendu que la
 * coquille, qui bascule le thème vers celui de l'élève dans son effet de mise en page : l'écran a
 * déjà lu ses couleurs, et ses glyphes, qui ne s'abonnent au thème (`useUnistyles`) que dans leurs
 * propres effets, passés après celui de la coquille, sœur aînée, ne l'apprennent jamais — l'encre
 * marine restait sur la nuit (l'énoncé, la flèche de retour, les glyphes de l'en-tête).
 *
 * Deux sondes encadrent l'écran dans sa mise en page : la première lit le thème que les autres ont
 * posé AVANT lui, la seconde celui où il sera peint, une fois passées ses propres bascules. L'écran
 * n'est remonté (une fois, avant la première peinture) que si le thème de la peinture n'est pas celui
 * de son rendu ET que ce n'est pas lui qui l'a posé :
 *  - à froid, la coquille a basculé avant lui : il est remonté ;
 *  - un écran qui bascule lui-même le thème (le live a sa propre portée) : rien ne se remonte, ses
 *    glyphes déjà abonnés suivent ;
 *  - l'entrée dans le live depuis la coquille : la coquille, qui sort dans le même commit, rend la
 *    main au thème d'avant l'espace élève, puis le live reprend celui de l'élève, celui de son rendu.
 *    Rien ne se remonte : remonter le live rejouait son entrée dans l'appel (une sortie, une seconde
 *    entrée, et une fausse coupure relevée).
 *
 * Chargé par le pont à la première route allumée seulement : éteint, Unistyles n'est pas lu ici.
 */
import React, { useLayoutEffect, useRef, useState } from "react"
import { UnistylesRuntime } from "react-native-unistyles"

import { type StudentSpaceScreenProps } from "./studentSpaceRouteTypes"

type Props = StudentSpaceScreenProps & { Screen: React.ComponentType<StudentSpaceScreenProps> }

export function ThemeSettledScreen(props: Props) {
    const { Screen, route } = props
    const [renderedTheme] = useState(() => UnistylesRuntime.themeName)
    const [settledTheme, setSettledTheme] = useState<string | undefined>(undefined)
    const beforeScreenRef = useRef<string | undefined>(undefined)

    return (
        <>
            <ThemeProbe
                onFirstLayout={(theme) => {
                    beforeScreenRef.current = theme
                }}
            />
            <Screen key={settledTheme} route={route} />
            <ThemeProbe
                onFirstLayout={(painted) => {
                    const switchedBeforeScreen = painted === beforeScreenRef.current
                    if (painted != null && painted !== renderedTheme && switchedBeforeScreen) setSettledTheme(painted)
                }}
            />
        </>
    )
}

/** Le thème lu dans la mise en page, au premier rendu seulement, à la place où la sonde est posée. */
function ThemeProbe(props: { onFirstLayout: (theme: string | undefined) => void }) {
    const { onFirstLayout } = props
    useLayoutEffect(() => {
        onFirstLayout(UnistylesRuntime.themeName)
        // Une seule fois : ensuite, les glyphes montés suivent le thème par leur abonnement.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    return null
}
